import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Flex } from '@televet/kibble-ui/chakra';
import { useUser } from '@/providers/user';
import { useFeatureFlag } from '@/lib/useFeatureFlag';
import { Home } from '@/components/Home/Home';
import { useScribeWindow } from '@/hooks/state/useScribeWindow';
import { ASTRO_AI_SCRIBE_FLAG_KEY } from '@/components/Scribe/constants';

export const Route = createFileRoute('/_authenticated/home')({
  component: RouteComponent,
});

/**
 * Mirrors Flow's `pages/Home` — a thin wrapper around the federated `Home` module with host
 * behaviours passed as callbacks. Both shells render the same component at the same path, so
 * E2E written against either drives the code the other ships.
 *
 * Full-height wrapper (mirrors Programs/Pilot) so astro's HomePage h="100%" resolves.
 */
function RouteComponent() {
  const { currentClinic, user } = useUser();
  const { isEnabled: isAstroScribeEnabled } = useFeatureFlag(
    ASTRO_AI_SCRIBE_FLAG_KEY,
    currentClinic.id ?? undefined
  );
  const { openScribeWindow } = useScribeWindow();
  const navigate = useNavigate();

  const onCampaignClick = (campaignId: string): void => {
    navigate({ to: '/programs/$', params: { _splat: campaignId } });
  };

  // The template id, not the text: creating from the template is what records the click.
  const onQuestionClick = (question: string, promptTemplateId?: string): void => {
    navigate({
      to: '/pilot',
      search: promptTemplateId ? { promptTemplateId } : { prompt: question },
    });
  };

  const onAskAnythingClick = (prompt?: string): void => {
    navigate({ to: '/pilot', search: prompt ? { prompt } : {} });
  };

  return (
    <Flex w="100%" h="100%" overflow="auto" maxW="100%">
      {/*
        Omitted on purpose, with no equivalent in this shell:
        - onAppointmentClick — Flow opens its own modal from the edit button in astro's appointment
          modal; without it that button is hidden.
        - showInviteButton — Flow gates it on `isUserAnAdmin`, and this shell has no role concept.
          Its "Add Members" CTA targets Settings → Clinic Team, which does not exist here, so
          leaving the flag unset keeps the banner from rendering rather than rendering it dead.

        onScribeOpen is passed only for clinics on AstroAiScribe, the same gate Flow uses to pick
        this window over its own, so the card's AI Scribe button stays inert for everyone else.
      */}
      <Home
        clinicId={currentClinic.id ?? undefined}
        userName={user?.fullName}
        onCampaignClick={onCampaignClick}
        onQuestionClick={onQuestionClick}
        onAskAnythingClick={onAskAnythingClick}
        onScribeOpen={isAstroScribeEnabled ? openScribeWindow : undefined}
      />
    </Flex>
  );
}
